import {
	useMutation,
	useQuery,
	type UseMutationResult,
	type UseQueryOptions,
	type UseQueryResult,
} from '@tanstack/react-query';
import { SERVER_ROUTES } from '../../../shared/constants/routes.constants';
import {
	type GetDevicesResponseSchemaType,
	type LoginResponseSchemaType,
	type LogoutDeviceResponseSchemaType,
	type LogoutResponseSchemaType,
} from '../../../shared/schemas/authSchemas/authResponses.schema';
import { generatePath } from '../../../shared/services/app.shared-service';
import type { ErrorResponse } from '../../../shared/types/error';
import {
	ACCESS_TOKEN_STORAGE_KEY,
	USER_STORAGE_KEY,
} from '../constants/auth.constants';
import { useAuthStore } from '../stores/useAuthStore';
import type {
	LoginSchemaType,
	LogoutDeviceRequestParamsSchemaType,
} from './../../../shared/schemas/authSchemas/authRequests.schema';
import { axiosClient, queryClient } from './api';

const AUTH_QUERY_KEY = 'auth';
const baseUrl = [SERVER_ROUTES.AUTH.version, SERVER_ROUTES.AUTH.BASE];

async function login(
	data: LoginSchemaType
): Promise<Omit<LoginResponseSchemaType, 'success'>> {
	const response = await axiosClient.post<LoginResponseSchemaType>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.AUTH.ROUTES.LOGIN],
		}),
		data,
		{ withCredentials: true }
	);
	return response.data;
}

export function useLogin(): UseMutationResult<
	Omit<LoginResponseSchemaType, 'success'>,
	ErrorResponse,
	LoginSchemaType
> {
	const setAuthenticated = useAuthStore((s) => s.setAuthenticated);

	return useMutation({
		retry: 0,
		mutationFn: (data) => login(data),
		onSuccess: (data) => {
			localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, data.accessToken);
			localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
			setAuthenticated(data.user);
		},
	});
}

async function logout(): Promise<LogoutResponseSchemaType['success']> {
	const response = await axiosClient.get<LogoutResponseSchemaType>(
		generatePath({ route: [...baseUrl, SERVER_ROUTES.AUTH.ROUTES.LOGOUT] }),
		{ withCredentials: true }
	);

	return response.data.success;
}

export function useLogout(): UseMutationResult<
	LogoutResponseSchemaType['success'],
	ErrorResponse,
	void
> {
	return useMutation({
		retry: 0,
		mutationFn: logout,
	});
}

async function getUserConnectedDevices(): Promise<
	Omit<GetDevicesResponseSchemaType, 'success'>
> {
	const response = await axiosClient.get<GetDevicesResponseSchemaType>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.AUTH.ROUTES.DEVICES],
		}),
		{
			withCredentials: true,
		}
	);

	return response.data;
}

export function useUserConnectedDevices(
	options?: UseQueryOptions<
		Omit<GetDevicesResponseSchemaType, 'success'>,
		ErrorResponse
	>
): UseQueryResult<
	Omit<GetDevicesResponseSchemaType, 'success'>,
	ErrorResponse
> {
	return useQuery<
		Omit<GetDevicesResponseSchemaType, 'success'>,
		ErrorResponse
	>({
		queryKey: [AUTH_QUERY_KEY, SERVER_ROUTES.AUTH.ROUTES.DEVICES],
		queryFn: getUserConnectedDevices,
		...options,
	});
}

async function logoutDevice(
	deviceId: string
): Promise<LogoutDeviceResponseSchemaType['success']> {
	const response = await axiosClient.get<LogoutResponseSchemaType>(
		generatePath({
			route: [...baseUrl, SERVER_ROUTES.AUTH.ROUTES.LOGOUT_DEVICE],
			params: { id: deviceId },
		})
	);

	return response.data.success;
}

export function useLogoutDevice(): UseMutationResult<
	LogoutDeviceResponseSchemaType['success'],
	ErrorResponse,
	LogoutDeviceRequestParamsSchemaType['id']
> {
	return useMutation({
		retry: 0,
		mutationFn: (data) => logoutDevice(data),
		onSuccess: (data, id) => {
			if (data) {
				queryClient.setQueryData<
					Omit<GetDevicesResponseSchemaType, 'success'>
				>(
					[AUTH_QUERY_KEY, SERVER_ROUTES.AUTH.ROUTES.DEVICES],
					(oldData) => {
						if (!oldData) return;

						return {
							...oldData,
							devices: oldData.devices.filter(
								(device) => device.deviceId !== id
							),
						};
					}
				);
			}
		},
	});
}
